import { makeStyles } from '@material-ui/core/styles';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import type { Entity } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { entityRouteRef, useEntity } from '@backstage/plugin-catalog-react';
import { hasTechDocs } from './hasTechDocs';

/*
 * The main content column, empty. `OverviewLayout` renders this in place of
 * the column when an entity carries no content-type card — no TechDocs
 * readme, no code-insights cards, no relation tables (most of which are
 * disabled anyway; see `catalog.md`'s "Relations as chips"). Left alone, that
 * column is a blank space beside a full rail — the redesign's least finished
 * state, since every mock frame was drawn against a Repository with a readme.
 *
 * Kept a plain, code-driven panel rather than an illustration: the rest of
 * the portal has no empty-state artwork to match, and a lone SVG here would
 * be its own maintenance surface.
 *
 * `Group` and `User` never carry TechDocs — the tab itself is hidden on both
 * kinds (see `docs/plugins/Hone/techdocs.md`) — so pointing them at the
 * TechDocs setup docs would be a dead end.
 */
const DOCS_EXCLUDED_KINDS = new Set(['group', 'user']);

const TECHDOCS_SETUP_URL = 'https://backstage.io/docs/features/techdocs/';
const ENTITY_FIELDS_URL =
  'https://backstage.io/docs/features/software-catalog/descriptor-format/';

/** Copy and next-action link for the empty content state. */
export type EmptyContentGuidance = {
  heading: string;
  body: string;
  actionLabel: string;
  actionHref: string;
  /** Whether the action leaves the portal, for the glyph that follows it. */
  external: boolean;
};

/**
 * Resolves the empty-state guidance for an entity: kind first (`Group`/`User`
 * never carry TechDocs), then whether TechDocs already exists, then setup.
 *
 * `techDocsHref` is the entity's own TechDocs tab — computed by the caller,
 * since building it needs the router — and is only read on the "already has
 * TechDocs" branch.
 */
export function emptyContentGuidance(
  entity: Entity,
  techDocsHref: string,
): EmptyContentGuidance {
  if (DOCS_EXCLUDED_KINDS.has(entity.kind.toLocaleLowerCase('en-US'))) {
    return {
      heading: 'Nothing to show here yet',
      body: 'This page has no overview content. Relations, links, and ownership come from the entity’s catalog-info.yaml — add them there and they will show up in the rail.',
      actionLabel: 'Entity descriptor reference',
      actionHref: ENTITY_FIELDS_URL,
      external: true,
    };
  }
  if (hasTechDocs(entity)) {
    return {
      heading: 'TechDocs already published',
      body: 'This entity’s documentation lives on the TechDocs tab — there’s nothing else to show on this overview yet.',
      actionLabel: 'View TechDocs',
      actionHref: techDocsHref,
      external: false,
    };
  }
  return {
    heading: 'No content yet',
    body: 'This entity has no TechDocs or readme to show here. Add a docs/ folder and a backstage.io/techdocs-ref annotation to its catalog-info.yaml to publish TechDocs here.',
    actionLabel: 'Set up TechDocs',
    actionHref: TECHDOCS_SETUP_URL,
    external: true,
  };
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'center',
    textAlign: 'center',
    gap: 12,
    padding: '48px 32px',
    borderRadius: 12,
    background: 'var(--bui-bg-neutral-2)',
    boxShadow: 'var(--hone-portal-elevation-card)',
  },
  /*
   * The one hone-yellow touch in the panel, per the redesign's rule that the
   * colour is punctuation rather than a fill — a pale-yellow badge behind a
   * neutral glyph, not a yellow icon or a yellow panel.
   */
  glyph: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 10,
    background: 'var(--helix-colors-brand-50)',
    color: 'var(--hone-portal-fg-body)',
    fontSize: 18,
  },
  heading: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.4,
    color: 'var(--bui-fg-primary)',
  },
  body: {
    fontSize: 12.5,
    lineHeight: 1.6,
    color: 'var(--bui-fg-secondary)',
    maxWidth: 380,
  },
  action: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    fontSize: 12.5,
    fontWeight: 600,
  },
  externalGlyph: {
    fontSize: 11,
  },
});

export function EmptyContentState() {
  const classes = useStyles();
  const { entity } = useEntity();
  const entityRoute = useRouteRef(entityRouteRef);
  const basePath = entityRoute?.({
    kind: entity.kind,
    namespace: entity.metadata.namespace ?? 'default',
    name: entity.metadata.name,
  });
  const techDocsHref = basePath ? `${basePath}/docs` : '';

  const { heading, body, actionLabel, actionHref, external } =
    emptyContentGuidance(entity, techDocsHref);

  return (
    <div className={classes.root}>
      <span className={classes.glyph} aria-hidden="true">
        <DescriptionOutlinedIcon fontSize="inherit" />
      </span>
      <h2 className={classes.heading}>{heading}</h2>
      <span className={classes.body}>{body}</span>
      <Link className={classes.action} to={actionHref}>
        {actionLabel}
        <span className={classes.externalGlyph} aria-hidden="true">
          {external ? '↗' : '›'}
        </span>
      </Link>
    </div>
  );
}
